import {
  type PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Types
export interface Document {
  id: string;
  name: string;
  type: 'bylaw' | 'financial' | 'meeting' | 'policy' | 'other';
  size: number;
  uploadedBy: string;
  uploadedAt: string;
  url: string;
  category: string;
  tags: string[];
  communityId: string;
  description?: string;
  version?: number;
  isPublic: boolean;
}

interface DocumentsState {
  loadingStates: {
    fetch: boolean;
    upload: boolean;
    delete: boolean;
  };
  error: string | null;
  filters: {
    type: string | null;
    category: string | null;
    searchQuery: string;
  };
  selectedDocumentId: string | null;
}

// Entity adapter for normalized state
const documentsAdapter = createEntityAdapter<Document>({
  selectId: (document) => document.id,
  sortComparer: (a, b) => b.uploadedAt.localeCompare(a.uploadedAt),
});

// Async thunks
export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async (communityId: string) => {
    const response = await fetch(`/api/communities/${communityId}/documents`);
    if (!response.ok) throw new Error('Failed to fetch documents');
    return response.json();
  }
);

export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async ({ file, metadata }: { file: File; metadata: Partial<Document> }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify(metadata));

    const response = await fetch('/api/documents/upload', {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) throw new Error('Failed to upload document');
    return response.json();
  }
);

export const deleteDocument = createAsyncThunk(
  'documents/deleteDocument',
  async (documentId: string) => {
    const response = await fetch(`/api/documents/${documentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) throw new Error('Failed to delete document');
    return documentId;
  }
);

// Slice
const initialState: DocumentsState & ReturnType<typeof documentsAdapter.getInitialState> = {
  ...documentsAdapter.getInitialState(),
  loadingStates: {
    fetch: false,
    upload: false,
    delete: false,
  },
  error: null,
  filters: {
    type: null,
    category: null,
    searchQuery: '',
  },
  selectedDocumentId: null,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocumentFilter: (
      state,
      action: PayloadAction<{ key: keyof DocumentsState['filters']; value: string | null }>
    ) => {
      const key = action.payload.key;
      if (key === 'searchQuery') {
        state.filters[key] = action.payload.value || '';
      } else {
        state.filters[key] = action.payload.value;
      }
    },
    clearDocumentFilters: (state) => {
      state.filters = initialState.filters;
    },
    selectDocument: (state, action: PayloadAction<string | null>) => {
      state.selectedDocumentId = action.payload;
    },
    updateDocumentTags: (state, action: PayloadAction<{ id: string; tags: string[] }>) => {
      documentsAdapter.updateOne(state, {
        id: action.payload.id,
        changes: { tags: action.payload.tags },
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch documents
      .addCase(fetchDocuments.pending, (state) => {
        state.loadingStates.fetch = true;
        state.error = null;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loadingStates.fetch = false;
        documentsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loadingStates.fetch = false;
        state.error = action.error.message || 'Failed to fetch documents';
      })
      // Upload document
      .addCase(uploadDocument.pending, (state) => {
        state.loadingStates.upload = true;
        state.error = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.loadingStates.upload = false;
        documentsAdapter.addOne(state, action.payload);
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.loadingStates.upload = false;
        state.error = action.error.message || 'Failed to upload document';
      })
      // Delete document
      .addCase(deleteDocument.pending, (state) => {
        state.loadingStates.delete = true;
        state.error = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loadingStates.delete = false;
        documentsAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loadingStates.delete = false;
        state.error = action.error.message || 'Failed to delete document';
      });
  },
});

// Actions
export const { setDocumentFilter, clearDocumentFilters, selectDocument, updateDocumentTags } =
  documentsSlice.actions;

// Selectors
const documentsSelectors = documentsAdapter.getSelectors((state: RootState) => state.documents);

export const selectAllDocuments = documentsSelectors.selectAll;
export const selectDocumentById = documentsSelectors.selectById;
export const selectDocumentsTotal = documentsSelectors.selectTotal;

export const selectFilteredDocuments = (state: RootState) => {
  let documents = selectAllDocuments(state);
  const { type, category, searchQuery } = state.documents.filters;

  if (type) {
    documents = documents.filter((doc) => doc.type === type);
  }

  if (category) {
    documents = documents.filter((doc) => doc.category === category);
  }

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    documents = documents.filter(
      (doc) =>
        doc.name.toLowerCase().includes(query) ||
        doc.description?.toLowerCase().includes(query) ||
        doc.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  }

  return documents;
};

export const selectDocumentLoadingStates = (state: RootState) => state.documents.loadingStates;
export const selectDocumentError = (state: RootState) => state.documents.error;
export const selectSelectedDocument = (state: RootState) =>
  state.documents.selectedDocumentId
    ? selectDocumentById(state, state.documents.selectedDocumentId)
    : null;

// Reducer
export default documentsSlice.reducer;
